import { createElement, useMemo } from 'react';

import uniq from 'lodash-es/uniq';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { BrowserIcon } from '@/components/atoms/Icons/BrowserIcon/BrowserIcon';
import { PlatformIcon } from '@/components/atoms/Icons/PlatformIcon/PlatformIcon';
import { IntegrationIcon } from '@/components/organisms/Integrations';
import { useConversationContext } from '@/hooks/useConversationContext';
import { useDeskId } from '@/hooks/useDeskId';
import { selectSessionId } from '@/redux/session/selectors';
import { getCountryByCode, getLanguageByLocale, noop } from '@/util/util';

import { useConversationDetail } from './useConversations';
import { IconByField } from '../components/RightSidebar/Sections/InformationSection';
import { MenuItem } from '../models';

export const useInformationSection = () => {
  const deskId = useDeskId();
  const sessionId = useSelector(selectSessionId);
  const { conversation } = useConversationDetail({ deskId, sessionId });
  const { t } = useTranslation();
  const { user } = useConversationContext();

  const { display_name, location, phone, locales, address, email, browser, timezone, platform } =
    user || {};
  const country = location?.country;

  const languages = useMemo(() => {
    return (
      uniq(locales?.map((locale) => getLanguageByLocale(locale)).filter((l) => !!l)).join(', ') ||
      undefined
    );
  }, [locales]);

  const cityAndCountry = useMemo(() => {
    return (
      [location?.city, location?.region, getCountryByCode(country)].filter((l) => !!l).join(', ') ||
      undefined
    );
  }, [location, country]);

  const usersTime = useMemo(() => {
    const options = {
      timeZone: timezone,
      hour: 'numeric' as const,
      minute: 'numeric' as const,
      hour12: true,
      timeZoneName: 'short' as const,
    };
    const formatter = new Intl.DateTimeFormat([], options);
    return (
      formatter
        .format(new Date())
        .split(' ')
        .map((x, index, array) => {
          if (index === 2) {
            return (array[index] = `(${x})`);
          }
          return x;
        })
        .join(' ') || '-'
    );
  }, [timezone]);

  const items: MenuItem[] = useMemo(
    () => [
      { id: 'display_name', text: display_name, onClick: noop, icon: IconByField.display_name },
      { id: 'email', text: email, onClick: noop, icon: IconByField.email, disabled: !email },
      {
        id: 'channel',
        text: conversation?.channel,
        onClick: noop,
        icon: createElement(IntegrationIcon, { type: conversation?.channel, size: 16 }),
      },
      {
        id: 'browser',
        text: browser,
        onClick: noop,
        icon: createElement(BrowserIcon, { browser: browser }),
      },
      {
        id: 'platform',
        text: platform,
        onClick: noop,
        icon: createElement(PlatformIcon, { platform: platform }),
      },
      { id: 'local_time', text: usersTime, onClick: noop, icon: IconByField.local_time },
      { id: 'languages', text: languages, onClick: noop, icon: IconByField.languages },
      {
        id: 'address',
        text: address,
        onClick: noop,
        icon: IconByField.address,
        disabled: !address,
      },
      { id: 'phone', text: phone, onClick: noop, icon: IconByField.phone, disabled: !phone },
      { id: 'location', text: cityAndCountry, onClick: noop, icon: IconByField.location },
    ],
    [
      address,
      cityAndCountry,
      conversation?.channel,
      display_name,
      email,
      languages,
      phone,
      platform,
      browser,
      usersTime,
    ]
  );

  const visibleItems = items
    .filter(
      (item) =>
        item.text ||
        item.id === 'display_name' ||
        item.id === 'phone' ||
        item.id === 'email' ||
        item.id === 'address'
    )
    .sort((a, b) => {
      const aEmpty = !a.text || a.text === t('common.na');
      const bEmpty = !b.text || b.text === t('common.na');
      return Number(aEmpty) - Number(bEmpty);
    });

  return { visibleItems };
};
